/**
 * Which dishes the Dish field searches for this workspace — the setting the
 * plan builder's Dish search switch reads and writes.
 *
 * Backed by TanStack Query, and shared: one setting for the workspace, so
 * every plan opened this session reads the same cached answer, and the
 * switch on one of them flips the switch on all of them.
 */

import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// A deep import rather than the dishes barrel: that barrel pulls in the whole
// Dishes screen and its upload dialogs, and this hook only needs the count.
import { useOwnDishCount } from "../../dishes/hooks/useOwnDishCount";
import { fetchDishScope, saveDishScope } from "../services/foodsService";
import type { DishScope } from "../types/plans.types";

export interface DishScopeState {
  /**
   * The setting as the switch should draw it. The whole catalog until the
   * read lands — which is what an un-narrowed search covers, so the switch
   * never opens claiming a narrowing it has not been told about.
   */
  scope: DishScope;
  /**
   * Changes it. The switch flips at once and the server is told after, so
   * pressing it never waits on the network; if the server refuses, the
   * switch goes back and the error is thrown for the screen to say so.
   */
  setScope: (scope: DishScope) => Promise<void>;
  /**
   * Whether the search may be kept to your own dishes at all: only once the
   * workspace has at least one, since a search narrowed to none finds nothing
   * and looks broken. `undefined` while the count is still being read, which
   * a control should treat as "not yet" rather than as either answer.
   *
   * Widening is never gated — a workspace that has since deleted its every
   * dish must still be able to open the search back up.
   */
  canNarrow: boolean | undefined;
}

export const DISH_SCOPE_QUERY_KEY = ["foods", "dish-scope"] as const;

export function useDishScope(): DishScopeState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();
  const ownDishCount = useOwnDishCount();

  const query = useQuery({
    queryKey: DISH_SCOPE_QUERY_KEY,
    queryFn: ({ signal }) => fetchDishScope(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("Dish search setting failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  const setScope = useCallback(
    async (scope: DishScope) => {
      const previous = queryClient.getQueryData<DishScope>(DISH_SCOPE_QUERY_KEY);
      queryClient.setQueryData<DishScope>(DISH_SCOPE_QUERY_KEY, scope);
      try {
        // What the server settled on rather than what was asked, the way the
        // billing profile is written back.
        const stored = await saveDishScope(getToken, scope);
        queryClient.setQueryData<DishScope>(DISH_SCOPE_QUERY_KEY, stored);
      } catch (error) {
        queryClient.setQueryData<DishScope | undefined>(
          DISH_SCOPE_QUERY_KEY,
          previous
        );
        throw error;
      }
    },
    [getToken, queryClient]
  );

  return {
    scope: query.data ?? "all",
    setScope,
    canNarrow: ownDishCount === undefined ? undefined : ownDishCount > 0,
  };
}

export default useDishScope;
